import React, { useState, useRef, useCallback, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import { Modal, Button } from 'semantic-ui-react';

// Default: A4 portrait — same ratio as ReportPreview's own cover page
// (A4_W_PX / A4_H_PX) — so the frame previewed here matches what's shown
// live. Callers whose cover renders into a differently-shaped box (e.g.
// MultiProgramPreview's shorter, wider cover) pass their own aspectRatio so
// what's previewed here matches where it actually lands.
const DEFAULT_ASPECT = 794 / 1123; // width / height
const STAGE_HEIGHT = 520;
const MIN_CROP_PCT = 10; // stop handles shrinking the crop line to nothing

const CORNERS = ['tl', 'tr', 'bl', 'br'];
const EDGES = ['top', 'bottom', 'left', 'right'];

/**
 * CoverImageCropModal
 *
 * `image` (the photo's own position/size) and `crop` (the crop line) are
 * both kept in independent, page-percentage coordinates so cropping never
 * rescales/distorts the photo — but they're driven by two different kinds
 * of handle, matching what a "crop" is supposed to mean:
 *  - The crop line's 4 edge handles (bars) trim ONE side at a time — only
 *    `crop` changes, revealing/hiding more of the (otherwise untouched)
 *    photo through the now-clipped frame.
 *  - The crop line's 4 corner handles resize `crop` proportionally (locked
 *    to its own aspect ratio) — again, only `crop` changes.
 *  - Everything outside `crop` is hidden (clipped), not just dimmed — once
 *    a crop is set, the parts of the source photo outside it are gone from
 *    view, same as the live cover page.
 *  - Dragging the now-clipped photo body moves `image` AND `crop` together
 *    by the same delta — the cropped result repositions as a single unit,
 *    rather than panning the source photo underneath a fixed crop window.
 *  - The single round handle (offset just outside the crop's bottom-right
 *    corner, so it doesn't overlap that corner's own crop-resize handle)
 *    scales `image` AND `crop` together proportionally, anchored at the
 *    crop's top-left — resizing the cropped result as a whole (its framing
 *    relative to the photo stays fixed) rather than zooming the photo
 *    independently of what's cropped.
 *
 * An earlier version let the photo pan/zoom fully independently of the crop
 * line (drag the photo under a fixed crop window, like typical crop tools).
 * That's not what was wanted here — once something is cropped, moving or
 * resizing it should move/resize the cropped result as a whole.
 *
 * Deliberately NOT built on react-easy-crop: that library enforces
 * "always cover one fixed frame, never any gaps," which doesn't support an
 * independently placed/sized crop rectangle at all.
 *
 * Props:
 *   open        {boolean}
 *   imageUrl    {string}
 *   initialImage {{xPct,yPct,widthPct,heightPct}}  % of the page — undefined
 *                for a freshly-picked image (computed as a "contain" fit)
 *   initialCrop {{xPct,yPct,widthPct,heightPct}}  % of the page — undefined
 *                for a freshly-picked image (defaults to match initialImage,
 *                so the crop line starts traced around the photo's own edges)
 *   aspectRatio {number}  width/height of the box this cover actually
 *                renders into — defaults to A4 portrait (ReportPreview.jsx's
 *                own cover). Pass the caller's real cover aspect ratio so
 *                the frame previewed here matches where it lands (e.g.
 *                MultiProgramPreview's shorter, wider cover box).
 *   onCancel    {function}
 *   onSave      {function}  ({image, crop}) => void
 */
let CoverImageCropModal = ({
  t,
  open,
  imageUrl,
  initialImage,
  initialCrop,
  aspectRatio = DEFAULT_ASPECT,
  onCancel,
  onSave,
}) => {
  const [image, setImage] = useState(null); // % of the page
  const [crop, setCrop] = useState(null); // % of the page
  const dragRef = useRef(null);
  const STAGE_WIDTH = Math.round(STAGE_HEIGHT * aspectRatio);

  // Reset when the modal opens for a (possibly new) image. With no saved
  // state yet: probe the image to compute an initial "contain" fit — whole
  // image visible, centred — and set the crop line to trace exactly around
  // it (no cropping applied yet).
  useEffect(() => {
    if (!open || !imageUrl) return;
    if (initialImage && initialCrop) {
      setImage(initialImage);
      setCrop(initialCrop);
      return;
    }
    setImage(null);
    setCrop(null);
    let cancelled = false;
    const probe = new window.Image();
    probe.onload = () => {
      if (cancelled) return;
      const imgAspect = probe.naturalWidth / probe.naturalHeight;
      let widthPct, heightPct;
      if (imgAspect > aspectRatio) {
        widthPct = 100;
        heightPct = 100 * (aspectRatio / imgAspect);
      } else {
        heightPct = 100;
        widthPct = 100 * (imgAspect / aspectRatio);
      }
      const fit = {
        xPct: (100 - widthPct) / 2,
        yPct: (100 - heightPct) / 2,
        widthPct,
        heightPct,
      };
      setImage(fit);
      setCrop({ ...fit });
    };
    probe.src = imageUrl;
    return () => {
      cancelled = true;
    };
  }, [open, imageUrl, initialImage, initialCrop, aspectRatio]);

  const handleMouseMove = useCallback(
    (e) => {
      const d = dragRef.current;
      if (!d) return;
      const dxPct = ((e.clientX - d.startX) / STAGE_WIDTH) * 100;
      const dyPct = ((e.clientY - d.startY) / STAGE_HEIGHT) * 100;

      if (d.mode === 'unit-move') {
        setImage({
          ...d.startImage,
          xPct: d.startImage.xPct + dxPct,
          yPct: d.startImage.yPct + dyPct,
        });
        setCrop({
          ...d.startCrop,
          xPct: d.startCrop.xPct + dxPct,
          yPct: d.startCrop.yPct + dyPct,
        });
        return;
      }
      if (d.mode === 'unit-resize') {
        // Scales the crop line AND the photo together, anchored at the crop
        // line's own top-left — the cropped result grows/shrinks as one
        // piece, with its framing relative to the photo held fixed (unlike
        // the crop corner handles below, which resize just the crop line and
        // do change how much of the photo is framed).
        const scaleFromW =
          (d.startCrop.widthPct + dxPct) / d.startCrop.widthPct;
        const scaleFromH =
          (d.startCrop.heightPct + dyPct) / d.startCrop.heightPct;
        const rawScale =
          Math.abs(dxPct) > Math.abs(dyPct) ? scaleFromW : scaleFromH;
        const scale = Math.max(
          MIN_CROP_PCT / d.startCrop.widthPct,
          MIN_CROP_PCT / d.startCrop.heightPct,
          rawScale
        );
        setCrop({
          xPct: d.startCrop.xPct,
          yPct: d.startCrop.yPct,
          widthPct: d.startCrop.widthPct * scale,
          heightPct: d.startCrop.heightPct * scale,
        });
        // Photo's offset from the crop's top-left scales by the same factor,
        // so the crop keeps showing the exact same relative slice of it.
        const offsetX = d.startImage.xPct - d.startCrop.xPct;
        const offsetY = d.startImage.yPct - d.startCrop.yPct;
        setImage({
          xPct: d.startCrop.xPct + offsetX * scale,
          yPct: d.startCrop.yPct + offsetY * scale,
          widthPct: d.startImage.widthPct * scale,
          heightPct: d.startImage.heightPct * scale,
        });
        return;
      }
      if (d.mode.startsWith('crop-edge-')) {
        const edge = d.mode.slice('crop-edge-'.length);
        const next = { ...d.startCrop };
        if (edge === 'right') {
          next.widthPct = Math.max(MIN_CROP_PCT, d.startCrop.widthPct + dxPct);
        } else if (edge === 'left') {
          const clampedWidth = Math.max(
            MIN_CROP_PCT,
            d.startCrop.widthPct - dxPct
          );
          next.xPct = d.startCrop.xPct + (d.startCrop.widthPct - clampedWidth);
          next.widthPct = clampedWidth;
        } else if (edge === 'bottom') {
          next.heightPct = Math.max(
            MIN_CROP_PCT,
            d.startCrop.heightPct + dyPct
          );
        } else if (edge === 'top') {
          const clampedHeight = Math.max(
            MIN_CROP_PCT,
            d.startCrop.heightPct - dyPct
          );
          next.yPct =
            d.startCrop.yPct + (d.startCrop.heightPct - clampedHeight);
          next.heightPct = clampedHeight;
        }
        setCrop(next);
        return;
      }
      if (d.mode.startsWith('crop-corner-')) {
        // Proportional resize locked to the crop line's own aspect ratio,
        // anchored at the opposite corner (which stays fixed). Only `crop`
        // changes — this is what actually adjusts the cropped selection.
        const corner = d.mode.slice('crop-corner-'.length);
        const isRight = corner.includes('r');
        const isBottom = corner.includes('b');
        const widthDelta = isRight ? dxPct : -dxPct;
        const heightDelta = isBottom ? dyPct : -dyPct;
        const scaleFromW =
          (d.startCrop.widthPct + widthDelta) / d.startCrop.widthPct;
        const scaleFromH =
          (d.startCrop.heightPct + heightDelta) / d.startCrop.heightPct;
        const rawScale =
          Math.abs(widthDelta) > Math.abs(heightDelta)
            ? scaleFromW
            : scaleFromH;
        const scale = Math.max(
          rawScale,
          MIN_CROP_PCT / d.startCrop.widthPct,
          MIN_CROP_PCT / d.startCrop.heightPct
        );
        const widthPct = d.startCrop.widthPct * scale;
        const heightPct = d.startCrop.heightPct * scale;
        setCrop({
          widthPct,
          heightPct,
          xPct: isRight
            ? d.startCrop.xPct
            : d.startCrop.xPct + d.startCrop.widthPct - widthPct,
          yPct: isBottom
            ? d.startCrop.yPct
            : d.startCrop.yPct + d.startCrop.heightPct - heightPct,
        });
      }
    },
    [STAGE_WIDTH]
  );

  const handleMouseUp = useCallback(() => {
    dragRef.current = null;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  const startDrag = useCallback(
    (mode) => (e) => {
      if (!image || !crop) return;
      e.preventDefault();
      e.stopPropagation();
      dragRef.current = {
        mode,
        startX: e.clientX,
        startY: e.clientY,
        startImage: image,
        startCrop: crop,
      };
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [image, crop, handleMouseMove, handleMouseUp]
  );

  // Clean up listeners if the modal is dismissed mid-drag
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const handleSave = () => {
    if (!image || !crop) return;
    onSave({ image, crop });
  };

  return (
    <Modal
      open={open}
      onClose={onCancel}
      size="small"
      className="cover-crop-modal"
    >
      <Modal.Header>{t('report.builder.position_cover_image')}</Modal.Header>
      <Modal.Content>
        <div
          className="cover-crop-stage"
          style={{ width: STAGE_WIDTH, height: STAGE_HEIGHT }}
        >
          {image && crop && (
            <>
              {/* Everything outside the crop line is hidden (clipped), not
                  just dimmed — same as the live cover page. The photo is
                  positioned in page-% terms re-expressed as a % of this
                  wrapper's own box (which sits at the crop's bounds), the
                  same conversion the live render uses. */}
              <div
                className="cover-crop-clip"
                style={{
                  left: `${crop.xPct}%`,
                  top: `${crop.yPct}%`,
                  width: `${crop.widthPct}%`,
                  height: `${crop.heightPct}%`,
                }}
              >
                <img
                  src={imageUrl}
                  alt=""
                  className="cover-crop-image"
                  onMouseDown={startDrag('unit-move')}
                  draggable={false}
                  style={{
                    left: `${
                      ((image.xPct - crop.xPct) / crop.widthPct) * 100
                    }%`,
                    top: `${
                      ((image.yPct - crop.yPct) / crop.heightPct) * 100
                    }%`,
                    width: `${(image.widthPct / crop.widthPct) * 100}%`,
                    height: `${(image.heightPct / crop.heightPct) * 100}%`,
                  }}
                />
              </div>
              <div
                className="cover-crop-window-border"
                style={{
                  left: `${crop.xPct}%`,
                  top: `${crop.yPct}%`,
                  width: `${crop.widthPct}%`,
                  height: `${crop.heightPct}%`,
                }}
              />

              {/* Edge handles — trim one side of the crop line at a time */}
              {EDGES.map((edge) => {
                const vertical = edge === 'top' || edge === 'bottom';
                const style =
                  edge === 'top'
                    ? {
                        left: `calc(${crop.xPct}% + ${
                          crop.widthPct / 2
                        }% - 10px)`,
                        top: `calc(${crop.yPct}% - 4px)`,
                      }
                    : edge === 'bottom'
                      ? {
                          left: `calc(${crop.xPct}% + ${
                            crop.widthPct / 2
                          }% - 10px)`,
                          top: `calc(${crop.yPct}% + ${crop.heightPct}% - 4px)`,
                        }
                      : edge === 'left'
                        ? {
                            left: `calc(${crop.xPct}% - 4px)`,
                            top: `calc(${crop.yPct}% + ${
                              crop.heightPct / 2
                            }% - 10px)`,
                          }
                        : {
                            left: `calc(${crop.xPct}% + ${crop.widthPct}% - 4px)`,
                            top: `calc(${crop.yPct}% + ${
                              crop.heightPct / 2
                            }% - 10px)`,
                          };
                return (
                  <div
                    key={edge}
                    className={`cover-crop-handle cover-crop-handle--edge-${
                      vertical ? 'h' : 'v'
                    }`}
                    onMouseDown={startDrag(`crop-edge-${edge}`)}
                    title={t(
                      `report.builder.photo_crop.drag_to_crop_the_${edge}`
                    )}
                    style={style}
                  />
                );
              })}

              {/* Corner handles — resize the crop line proportionally,
                  changing what's framed (unlike the round handle below,
                  which resizes the already-cropped result as a whole) */}
              {CORNERS.map((corner) => {
                const isRight = corner.includes('r');
                const isBottom = corner.includes('b');
                const style = {
                  left: isRight
                    ? `calc(${crop.xPct}% + ${crop.widthPct}% - 8px)`
                    : `calc(${crop.xPct}% - 8px)`,
                  top: isBottom
                    ? `calc(${crop.yPct}% + ${crop.heightPct}% - 8px)`
                    : `calc(${crop.yPct}% - 8px)`,
                };
                return (
                  <div
                    key={corner}
                    className="cover-crop-handle cover-crop-handle--corner"
                    onMouseDown={startDrag(`crop-corner-${corner}`)}
                    title={t(
                      'report.builder.photo_crop.drag_to_resize_crop_line'
                    )}
                    style={style}
                  />
                );
              })}

              {/* Resizes the cropped result as a whole (image + crop
                  together) — offset outside the crop's own bottom-right
                  corner so it never overlaps that corner's crop-resize
                  handle. */}
              <div
                className="cover-crop-handle cover-crop-handle--unit"
                onMouseDown={startDrag('unit-resize')}
                title={t('report.builder.photo_crop.drag_hints')}
                style={{
                  left: `calc(${crop.xPct}% + ${crop.widthPct}% + 6px)`,
                  top: `calc(${crop.yPct}% + ${crop.heightPct}% + 6px)`,
                }}
              />
            </>
          )}
        </div>
        <p className="cover-crop-hint">
          {t('report.builder.photo_crop.hints')}
        </p>
      </Modal.Content>
      <Modal.Actions>
        <Button className="Secondary-Button" onClick={onCancel}>
          {t('actions.cancel')}
        </Button>
        <Button
          className="Primary-Button"
          onClick={handleSave}
          disabled={!image || !crop}
        >
          {t('actions.save')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

CoverImageCropModal = withTranslation()(CoverImageCropModal);

export default CoverImageCropModal;
